import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Info, Calendar } from 'lucide-react';
import {
  getKuendigungPreview,
  createKuendigung,
  type KuendigungPreview,
  type CreateKuendigungPayload,
  type ReasonCategory,
  type KontingentHandling,
  REASON_LABELS,
  KONTINGENT_HANDLING_LABELS,
} from '../../lib/kuendigungen';
import { formatDate } from '../../lib/dateFormat';
import WizardShell from '../ui/wizard-shell';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';

// ─── Props ──────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  teilnahmeId: number;
  standortId: number;
  onCreated?: (id: number) => void;
}

// ─── Weekday helper ─────────────────────────────────────

const WEEKDAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

function fristDescription(preview: KuendigungPreview): string {
  const calc = preview.effectiveDateCalc;
  if (!calc.fristType) return `Kündigungsfrist: ${calc.kuendigungsfristDays} Tage`;
  switch (calc.fristType) {
    case 'days_fixed':
      return `Kündigungsfrist: ${calc.fristValue} Tage`;
    case 'zum_monatsende':
      return `Kündigung zum Monatsende (${calc.fristValue} Monat${calc.fristValue !== 1 ? 'e' : ''} Vorlauf)`;
    case 'zum_quartalsende':
      return `Kündigung zum Quartalsende (${calc.fristValue} Monat${calc.fristValue !== 1 ? 'e' : ''} Vorlauf)`;
    case 'zum_periodenende':
      return `Kündigung zum Ende des Beitragszeitraums (${calc.fristValue} Monat${calc.fristValue !== 1 ? 'e' : ''} Vorlauf)`;
    default:
      return `Kündigungsfrist: ${calc.kuendigungsfristDays} Tage`;
  }
}

// ─── Component ──────────────────────────────────────────

const KuendigungWizardModal: React.FC<Props> = ({
  open,
  onOpenChange,
  teilnahmeId,
  standortId,
  onCreated,
}) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Preview data from backend
  const [preview, setPreview] = useState<KuendigungPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Schritt 2: Kündigungsdetails
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reasonCategory, setReasonCategory] = useState<ReasonCategory | ''>('');
  const [reasonText, setReasonText] = useState('');
  const [kontingentHandling, setKontingentHandling] = useState<KontingentHandling>('use_until_effective');
  const [notes, setNotes] = useState('');

  // Schritt 3: Kommunikation
  const [sendEmail, setSendEmail] = useState(true);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');

  // ─── Load preview when modal opens ────────────────────

  useEffect(() => {
    if (!open || !teilnahmeId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);

    getKuendigungPreview(teilnahmeId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setEffectiveDate(data.effectiveDateCalc.earliestDate);
        // Default kontingent handling
        if (data.kontingent && data.kontingent.remainingUnits > 0) {
          setKontingentHandling('use_until_effective');
        } else {
          setKontingentHandling('forfeit');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(err?.message || 'Vorschau konnte nicht geladen werden');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, teilnahmeId]);

  // ─── Reset on close ───────────────────────────────────

  useEffect(() => {
    if (!open) {
      setStep(1);
      setPreview(null);
      setPreviewError(null);
      setEffectiveDate('');
      setReasonCategory('');
      setReasonText('');
      setKontingentHandling('use_until_effective');
      setNotes('');
      setSendEmail(true);
      setEmailSubject('');
      setEmailContent('');
    }
  }, [open]);

  // ─── Validation ───────────────────────────────────────

  const canProceedStep1 = useMemo(() => {
    return !!preview && !previewLoading && !previewError && !preview.existingKuendigung;
  }, [preview, previewLoading, previewError]);

  const canProceedStep2 = useMemo(() => {
    return !!effectiveDate;
  }, [effectiveDate]);

  // ─── Navigation ───────────────────────────────────────

  const handleNext = () => {
    if (step === 1 && !canProceedStep1) {
      toast.error('Vorschau nicht verfügbar');
      return;
    }
    if (step === 2 && !canProceedStep2) {
      toast.error('Bitte wählen Sie ein Wirksamkeitsdatum');
      return;
    }
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // ─── Submit ───────────────────────────────────────────

  const handleSubmit = async () => {
    if (!preview) return;
    setIsLoading(true);
    try {
      const payload: CreateKuendigungPayload = {
        kursTeilnahmeId: teilnahmeId,
        effectiveDate,
        reasonCategory: reasonCategory || undefined,
        reasonText: reasonText || undefined,
        kontingentHandling,
        notes: notes || undefined,
        sendEmail,
        emailSubject: sendEmail ? (emailSubject || undefined) : undefined,
        emailContent: sendEmail ? (emailContent || undefined) : undefined,
      };

      const created = await createKuendigung(payload);
      if (created.communicationWarnings?.length) {
        toast.warning(
          `Kündigung erstellt, aber die E-Mail wurde nicht versendet: ${created.communicationWarnings.join(' ')}`,
        );
      } else {
        toast.success('Kündigung erfolgreich erstellt');
      }
      onOpenChange(false);
      if (onCreated) onCreated(created.id);
    } catch (error: any) {
      toast.error(error?.message || 'Fehler beim Erstellen der Kündigung');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── CommunicationComposer groups ─────────────────────

  const composerGroups: RecipientGroup[] = useMemo(() => {
    if (!preview) return [];
    const student = preview.student;
    if (!student.wpUserId || student.wpUserId <= 0) return [];

    const sp = preview.teilnahme.subprofileId;
    const wpUserId = student.wpUserId;
    let subIndex: number | null = null;
    if (sp && sp.includes('-')) {
      const parts = sp.split('-', 2);
      subIndex = parts[1] ? (Number.parseInt(parts[1], 10) || null) : null;
    }

    return [{
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{
        id: `person:${wpUserId}:${subIndex ?? ''}`,
        type: 'person' as const,
        wpUserId,
        subIndex,
        name: student.name || 'Teilnehmer',
      }],
      templateKeys: { email: 'kuendigung.confirmation.customer' },
      vars: {
        customerName: student.name || 'Teilnehmer',
        fachName: preview.kurs.fachName || preview.kurs.name,
        teacherName: preview.kurs.teacherName || '',
        effectiveDate: effectiveDate || preview.effectiveDateCalc.earliestDate,
        standortName: preview.kurs.standortName || '',
      },
      defaultSelectedChannels: { email: sendEmail, sms: false },
    }];
  }, [preview, effectiveDate, sendEmail]);

  // ─── Derived labels ───────────────────────────────────

  const weekdayLabel = preview?.kurs.weekday
    ? WEEKDAY_NAMES[(preview.kurs.weekday) - 1] || '—'
    : '—';

  const timeLabel = preview?.kurs.startTime
    ? preview.kurs.startTime.slice(0, 5) + ' Uhr'
    : '—';

  if (!open) return null;

  // ─── Render ───────────────────────────────────────────

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Kündigung erstellen"
      description={`Schritt ${step} von 4`}
      currentStep={step}
      steps={[
        { label: 'Kurs & Schüler' },
        { label: 'Details' },
        { label: 'Kommunikation' },
        { label: 'Übersicht' },
      ]}
      onBack={step > 1 ? handleBack : undefined}
      canBack={step > 1 && !isLoading}
      backLabel="Zurück"
      onNext={step < 4 ? handleNext : undefined}
      canNext={step === 1 ? canProceedStep1 : step === 2 ? canProceedStep2 : true}
      nextLabel="Weiter"
      onPrimary={step === 4 ? handleSubmit : undefined}
      primaryLabel={isLoading ? 'Wird erstellt...' : 'Kündigung bestätigen'}
      canPrimary={step === 4 && !isLoading}
      isLoading={isLoading}
    >
      <div className="py-4">

        {/* ─── Schritt 1: Kurs & Schüler ──────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            {previewLoading && (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                Lade Vorschau…
              </div>
            )}

            {previewError && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span>{previewError}</span>
              </div>
            )}

            {preview?.existingKuendigung && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span>
                  Für diese Kursteilnahme existiert bereits eine Kündigung
                  (Status: {preview.existingKuendigung.status}, wirksam ab {formatDate(preview.existingKuendigung.effectiveDate)}).
                </span>
              </div>
            )}

            {preview && !preview.existingKuendigung && (
              <WizardCard>
                <WizardSection title="Schüler">
                  <WizardKVGrid>
                    <WizardKVItem label="Name">{preview.student.name || '—'}</WizardKVItem>
                    <WizardKVItem label="E-Mail">{preview.student.email || '—'}</WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>

                <WizardSection title="Kursdetails">
                  <WizardKVGrid>
                    <WizardKVItem label="Kurs">{preview.kurs.name}</WizardKVItem>
                    {preview.kurs.fachName && (
                      <WizardKVItem label={getSubjectLabels().singular}>{preview.kurs.fachName}</WizardKVItem>
                    )}
                    {preview.kurs.teacherName && (
                      <WizardKVItem label="Lehrer">{preview.kurs.teacherName}</WizardKVItem>
                    )}
                    {preview.kurs.standortName && (
                      <WizardKVItem label="Standort">{preview.kurs.standortName}</WizardKVItem>
                    )}
                    <WizardKVItem label="Termin">
                      {weekdayLabel}, {timeLabel}
                      {preview.kurs.durationMin ? ` (${preview.kurs.durationMin} Min.)` : ''}
                    </WizardKVItem>
                    <WizardKVItem label="Teilnahme seit">
                      {formatDate(preview.teilnahme.effectiveFrom)}
                    </WizardKVItem>
                  </WizardKVGrid>
                </WizardSection>

                {preview.kontingent && (
                  <WizardSection title="Kontingent">
                    <WizardKVGrid>
                      {preview.kontingent.packageName && (
                        <WizardKVItem label="Paket">{preview.kontingent.packageName}</WizardKVItem>
                      )}
                      <WizardKVItem label="Verbraucht">
                        {preview.kontingent.usedUnits} / {preview.kontingent.totalUnits} Einheiten
                      </WizardKVItem>
                      <WizardKVItem label="Restguthaben">
                        <span className="font-medium">{preview.kontingent.remainingUnits} Einheiten</span>
                        {preview.kontingent.totalPaidCents > 0 && (
                          <span className="ml-1 text-muted-foreground">
                            ({formatCents(preview.kontingent.totalPaidCents)} bezahlt)
                          </span>
                        )}
                      </WizardKVItem>
                    </WizardKVGrid>
                  </WizardSection>
                )}
              </WizardCard>
            )}
          </div>
        )}

        {/* ─── Schritt 2: Kündigungsdetails ───────────── */}
        {step === 2 && preview && (
          <div className="space-y-4">
            {/* Frist-Info */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              <Info className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="font-medium">{fristDescription(preview)}</div>
                <div className="mt-1">
                  Frühestmögliches Wirksamkeitsdatum:{' '}
                  <span className="font-semibold">{formatDate(preview.effectiveDateCalc.earliestDate)}</span>
                </div>
              </div>
            </div>

            {/* Mindestlaufzeit-Hinweis */}
            {!preview.effectiveDateCalc.mindestlaufzeitOk && preview.effectiveDateCalc.mindestlaufzeitEndsAt && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <div className="font-medium">Mindestlaufzeit noch nicht erreicht</div>
                  <div className="mt-1">
                    Die Mindestlaufzeit endet am{' '}
                    <span className="font-semibold">{formatDate(preview.effectiveDateCalc.mindestlaufzeitEndsAt)}</span>.
                    Eine Kündigung ist frühestens zum berechneten Datum möglich.
                  </div>
                </div>
              </div>
            )}

            <WizardCard>
              {/* Wirksamkeitsdatum */}
              <WizardSection title="Wirksamkeitsdatum">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={effectiveDate}
                    min={preview.effectiveDateCalc.earliestDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    className="w-48"
                  />
                  {effectiveDate && effectiveDate !== preview.effectiveDateCalc.earliestDate && (
                    <span className="text-xs text-muted-foreground">
                      (frühestens: {formatDate(preview.effectiveDateCalc.earliestDate)})
                    </span>
                  )}
                </div>
              </WizardSection>

              {/* Kündigungsgrund */}
              <WizardSection title="Kündigungsgrund">
                <div className="space-y-3">
                  <Select
                    value={reasonCategory}
                    onValueChange={(v) => setReasonCategory(v as ReasonCategory)}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Grund auswählen (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(REASON_LABELS) as [ReasonCategory, string][]).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Optionale Erläuterung zum Kündigungsgrund…"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    rows={3}
                  />
                </div>
              </WizardSection>

              {/* Kontingent-Details & Handling (nur wenn Kontingent vorhanden) */}
              {preview.kontingent && (
                <WizardSection title="Kontingent">
                  <WizardKVGrid>
                    {preview.kontingent.packageName && (
                      <WizardKVItem label="Paket">{preview.kontingent.packageName}</WizardKVItem>
                    )}
                    <WizardKVItem label="Einheiten gesamt">{preview.kontingent.totalUnits}</WizardKVItem>
                    <WizardKVItem label="Verbraucht">{preview.kontingent.usedUnits}</WizardKVItem>
                    <WizardKVItem label="Verbleibend">
                      <span className={preview.kontingent.remainingUnits > 0 ? 'font-medium text-amber-600' : ''}>
                        {preview.kontingent.remainingUnits} Einheiten
                      </span>
                    </WizardKVItem>
                    {preview.kontingent.totalPaidCents > 0 && (
                      <WizardKVItem label="Bezahlter Betrag">
                        {formatCents(preview.kontingent.totalPaidCents)}
                      </WizardKVItem>
                    )}
                    {/* Was bei „Teilerstattung“ zurückgeht — das Backend rechnet es schon (T-0173). */}
                    {(preview.kontingent.refundCents ?? 0) > 0 && (
                      <WizardKVItem label="Erstattung bei Teilerstattung">
                        {formatCents(preview.kontingent.refundCents ?? 0)}
                        {preview.kontingent.unitPriceCents
                          ? ` (${preview.kontingent.remainingUnits} × ${formatCents(preview.kontingent.unitPriceCents)})`
                          : ''}
                      </WizardKVItem>
                    )}
                  </WizardKVGrid>

                  {preview.kontingent.remainingUnits > 0 && (
                    <div className="mt-4 space-y-2">
                      <Label className="text-sm font-medium">Behandlung des Restguthabens</Label>
                      {(['use_until_effective', 'forfeit', 'partial_refund'] as KontingentHandling[]).map((option) => (
                        <Label key={option} className="flex items-center gap-3 cursor-pointer rounded-lg border p-3 transition-colors hover:bg-muted/40">
                          <input
                            type="radio"
                            name="kontingentHandling"
                            value={option}
                            checked={kontingentHandling === option}
                            onChange={() => setKontingentHandling(option)}
                            className="shrink-0"
                          />
                          <span className="text-sm">{KONTINGENT_HANDLING_LABELS[option]}</span>
                        </Label>
                      ))}
                    </div>
                  )}
                </WizardSection>
              )}

              {/* Interne Notizen */}
              <WizardSection title="Interne Notizen">
                <Textarea
                  placeholder="Optionale interne Notizen…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </WizardSection>
            </WizardCard>
          </div>
        )}

        {/* ─── Schritt 3: Kommunikation ─────────────── */}
        {step === 3 && composerGroups.length > 0 && (
          <CommunicationComposer
            active={open && step === 3}
            standortId={standortId}
            groups={composerGroups}
            onDraftsChange={({ groups: g }) => {
              const s = g['student'];
              if (s) {
                setSendEmail(!!s.selectedChannels.email);
                setEmailSubject(s.email.subjectTpl || '');
                setEmailContent(s.email.htmlTpl || '');
              }
            }}
          />
        )}

        {step === 3 && composerGroups.length === 0 && (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            Kein Empfänger verfügbar. Bitte prüfen Sie die Schülerdaten.
          </div>
        )}

        {/* ─── Schritt 4: Zusammenfassung ────────────── */}
        {step === 4 && preview && (
          <WizardCard>
            <WizardSection title="Schüler & Kurs">
              <WizardKVGrid>
                <WizardKVItem label="Schüler">{preview.student.name || '—'}</WizardKVItem>
                <WizardKVItem label="Kurs">{preview.kurs.name}</WizardKVItem>
                {preview.kurs.fachName && (
                  <WizardKVItem label={getSubjectLabels().singular}>{preview.kurs.fachName}</WizardKVItem>
                )}
                {preview.kurs.teacherName && (
                  <WizardKVItem label="Lehrer">{preview.kurs.teacherName}</WizardKVItem>
                )}
                <WizardKVItem label="Termin">
                  {weekdayLabel}, {timeLabel}
                </WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Kündigungsdetails">
              <WizardKVGrid>
                <WizardKVItem label="Wirksamkeitsdatum">
                  <span className="font-medium">{formatDate(effectiveDate)}</span>
                </WizardKVItem>
                <WizardKVItem label="Kündigungsfrist">
                  {fristDescription(preview)}
                </WizardKVItem>
                {reasonCategory && (
                  <WizardKVItem label="Grund">
                    {REASON_LABELS[reasonCategory as ReasonCategory]}
                    {reasonText && <span className="ml-1 text-muted-foreground">— {reasonText}</span>}
                  </WizardKVItem>
                )}
                {!reasonCategory && (
                  <WizardKVItem label="Grund" className="text-muted-foreground">Kein Grund angegeben</WizardKVItem>
                )}
              </WizardKVGrid>
            </WizardSection>

            {preview.kontingent && preview.kontingent.remainingUnits > 0 && (
              <WizardSection title="Restguthaben">
                <WizardKVGrid>
                  <WizardKVItem label="Verbleibend">
                    {preview.kontingent.remainingUnits} Einheiten
                  </WizardKVItem>
                  <WizardKVItem label="Behandlung">
                    {KONTINGENT_HANDLING_LABELS[kontingentHandling]}
                  </WizardKVItem>
                  {kontingentHandling === 'partial_refund' && (preview.kontingent.refundCents ?? 0) > 0 && (
                    <WizardKVItem label="Erstattung">{formatCents(preview.kontingent.refundCents ?? 0)}</WizardKVItem>
                  )}
                </WizardKVGrid>
              </WizardSection>
            )}

            {notes && (
              <WizardSection title="Interne Notizen">
                <p className="text-sm whitespace-pre-wrap">{notes}</p>
              </WizardSection>
            )}

            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="Bestätigungs-E-Mail">
                  <WizardYesNoBadge yes={sendEmail} />
                </WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
          </WizardCard>
        )}

      </div>
    </WizardShell>
  );
};

export default KuendigungWizardModal;
